import { useState, useEffect, useMemo, useCallback } from "react";
import { sysConfigApi } from "../../../services/api/sysconfigService";
import { useTimeseriesSysconfig } from "./useTimeseriesSysconfig";
import {
  fetchResultsData,
  parseCSVWithWorker,
} from "../components/FileUpload/ResultsDisplay";
import {
  extractDemandChargeRows,
  hasDemandChargeConfig,
} from "../utils/demandCharge";

/**
 * Fetches sysconfig to determine whether demand_charge is configured, and
 * if so, downloads + parses the daily-summary CSV to produce an array of
 * { date, peakPowerKw, demandCharge, hasAttribution, productionDemand,
 *   consumptionDemand, storageDemand, unattributedDemand, deviceAttribution }
 * rows.
 *
 * `hasAttribution` is false for results produced before the sim split the
 * demand charge across the cost categories; those rows carry null for the four
 * category values so callers can render an em-dash rather than a fabricated 0.
 *
 * ── Per-device attribution ────────────────────────────────────────────────
 * Each row also carries `deviceAttribution`, the "<device> Demand Attribution
 * ($)" columns keyed by bare device name -- the figures the Storage and
 * Production DEVICE cards show, summed over a period with
 * `sumDeviceAttributionForPeriod`. They ride on the same projected rows as the
 * category figures rather than being handed back as a second, raw array: one
 * array, normalised once, sliced by one predicate, so a device card and the
 * summary card above it cannot describe different days. `null` on a run whose
 * daily summary carries no such column, never an empty object -- absent is not
 * "every device was nothing".
 *
 * @returns {{ hasDemandCharge: boolean, hasAttribution: boolean, hasDeviceAttribution: boolean, loading: boolean, dailyDemand: Array, totalDemandCharge: number }}
 */
export function useDemandChargeData() {
  const {
    storageAccount,
    microgridName,
    datasetName: resolvedDatasetName,
    loading: paramsLoading,
  } = useTimeseriesSysconfig();

  const token = sessionStorage.getItem("accessToken");
  const baseUrl = process.env.REACT_APP_BASE_URL;
  // Read the legacy misspelling as a fallback: the key was renamed from
  // "dailsummaryFileName", and a tab open across that change still holds the old
  // one. Without this a live session loses demand-charge data until the user
  // re-opens a simulation, since only that path rewrites the key. Also reject
  // the literal "null", which setItem stores when no daily-summary file matched.
  const rawDailySummaryFileName =
    sessionStorage.getItem("dailySummaryFileName") ||
    sessionStorage.getItem("dailsummaryFileName") ||
    "";
  const dailySummaryFileName =
    rawDailySummaryFileName === "null" ? "" : rawDailySummaryFileName;
  const selectedSimName =
    (() => {
      try {
        return JSON.parse(sessionStorage.getItem("selectedSimulation") || "{}")
          .name;
      } catch {
        return "";
      }
    })() || "";

  const [hasDemandCharge, setHasDemandCharge] = useState(false);
  const [dailyDemand, setDailyDemand] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSysconfig = useCallback(async () => {
    if (paramsLoading || !token || !storageAccount || !resolvedDatasetName)
      return;

    try {
      const data = await sysConfigApi({
        accountname: storageAccount,
        simulationName: microgridName,
        datasetName: resolvedDatasetName,
        token,
      });

      const meters = data?.Microgrid?.meters;
      if (!Array.isArray(meters) || meters.length === 0) {
        setHasDemandCharge(false);
        return;
      }

      const meter =
        meters.find(
          (m) =>
            (m.name || "").trim().toLowerCase() ===
            (selectedSimName || "").trim().toLowerCase(),
        ) || meters[0];

      const hasIt = hasDemandChargeConfig(meter);
      setHasDemandCharge(hasIt);
      sessionStorage.setItem("hasDemandCharge", hasIt ? "1" : "");
    } catch (err) {
      console.error("useDemandChargeData: sysconfig fetch failed", err);
      setHasDemandCharge(false);
      sessionStorage.setItem("hasDemandCharge", "");
    }
  }, [
    paramsLoading,
    token,
    storageAccount,
    microgridName,
    resolvedDatasetName,
    selectedSimName,
  ]);

  useEffect(() => {
    fetchSysconfig();
  }, [fetchSysconfig]);

  const fetchDailySummary = useCallback(async () => {
    if (!hasDemandCharge || !dailySummaryFileName) {
      setDailyDemand([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const csvText = await fetchResultsData(
        "Daily Summary",
        dailySummaryFileName,
        baseUrl,
        storageAccount,
        microgridName,
        token,
      );
      const { data: rows } = await parseCSVWithWorker(csvText);
      setDailyDemand(extractDemandChargeRows(rows));
    } catch (err) {
      console.error("useDemandChargeData: daily-summary fetch failed", err);
      setDailyDemand([]);
    } finally {
      setLoading(false);
    }
  }, [
    hasDemandCharge,
    dailySummaryFileName,
    baseUrl,
    storageAccount,
    microgridName,
    token,
  ]);

  useEffect(() => {
    fetchDailySummary();
  }, [fetchDailySummary]);

  const totalDemandCharge = useMemo(
    () => dailyDemand.reduce((sum, r) => sum + r.demandCharge, 0),
    [dailyDemand],
  );

  const hasAttribution = useMemo(
    () => dailyDemand.length > 0 && dailyDemand.every((r) => r.hasAttribution),
    [dailyDemand],
  );

  /* Whether the run carries the per-device columns at all. Same all-or-nothing
   * rule as hasAttribution: the sim writes one column per production and
   * storage device in a single pass, so a partial set would mean the per-device
   * figures no longer reconcile to the category split they are a decomposition
   * of. Individual devices can still be absent from a run that has the set --
   * that is `sumDeviceAttribution` returning null, and it is a per-device
   * question rather than a per-run one. */
  const hasDeviceAttribution = useMemo(
    () =>
      dailyDemand.length > 0 &&
      dailyDemand.every(
        (r) => r.deviceAttribution && Object.keys(r.deviceAttribution).length > 0,
      ),
    [dailyDemand],
  );

  return {
    hasDemandCharge,
    hasAttribution,
    hasDeviceAttribution,
    loading,
    dailyDemand,
    totalDemandCharge,
  };
}
